/**
 * Mise en mots de la durée d'une séance, pour le rapport de diagnostic.
 *
 * <p>Les trois endroits qui l'affichent — l'en-tête, la couverture du tirage et
 * la carte de synthèse — la calculaient chacun de leur côté, et de la même
 * façon fausse : `Math.max(1, Math.round(minutes / 60))`, soit une durée
 * toujours dite en heures, jamais moins d'une. Un test rendu en deux minutes
 * s'annonçait « une heure », et tout test de moins de quatre-vingt-dix minutes
 * affichait la même valeur — la carte « Durée » ne disait donc rien.</p>
 *
 * <p>L'unité suit désormais la grandeur : des minutes tant qu'on en compte
 * moins de deux heures, des heures au-delà. Le seuil est haut à dessein — une
 * épreuve dure une heure et demie sans être longue, et « 90 minutes » se lit
 * mieux que « 2 heures », qui l'arrondirait par excès.</p>
 */

/** Clé de traduction et variables, prêtes pour le pluriel. */
export interface ReportDurationLabel {
  key: 'summary.minutes' | 'summary.hours';
  vars: { count: number };
}

/** Au-delà, la durée se dit en heures. */
const HOURS_THRESHOLD_MINUTES = 120;

/**
 * @param minutes Durée en minutes, telle que l'API la rend.
 * @returns `null` quand il n'y a rien à annoncer — durée absente ou nulle.
 * L'appelant fait alors disparaître l'item plutôt que d'afficher une étiquette
 * sans valeur, ou un zéro qui se lirait comme un test non passé.
 */
export function reportDuration(minutes: number | null | undefined): ReportDurationLabel | null {
  if (minutes == null || minutes <= 0) return null;

  if (minutes < HOURS_THRESHOLD_MINUTES) {
    return { key: 'summary.minutes', vars: { count: Math.round(minutes) } };
  }

  return { key: 'summary.hours', vars: { count: Math.round(minutes / 60) } };
}
